import { FileDownload } from '@mui/icons-material';
import { Box, CircularProgress } from '@mui/material';
// eslint-disable-next-line no-restricted-imports -- client-side report, no server-side pagination
import { DataGridPro, useGridApiRef } from '@mui/x-data-grid-pro';
import { t } from 'i18next';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { fetchDigitalMaintenanceReportsInRange } from '@sophocles/api/reports';
import TimeRangeSelector from '@sophocles/components/TimeRangeSelector';
import DataGridToolbar, { ToolbarIconButton } from '@sophocles/data-grids/components/toolbar';
import useRelayLoadTerm from '@sophocles/hooks/useRelayLoadTerm';
import { getExcelFile } from '@sophocles/utils';

function relayCommandLabels() {
  return {
    RELAY_OFF: t('reports.relayOff', 'OFF'),
    RELAY_ON: t('reports.relayOn', 'ON'),
    REBOOT_RELAY: t('reports.rebootRelay', 'REBOOT'),
  };
}

function formatAction(command) {
  return relayCommandLabels()[command] || t('reports.invalidCommand', 'Invalid Command');
}

function formatUser(user) {
  const noUser = t('reports.noUser', 'No User');
  if (!user) return noUser;
  return `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim() || noUser;
}

function DigitalMaintenanceReportGrid({ baseStructureLink, isBillboard, siteGroupId, siteGroup }) {
  const { t } = useTranslation();
  const apiRef = useGridApiRef();
  const relayLoadTerm = useRelayLoadTerm();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(() => moment().subtract(1, 'month').toDate());
  const [endDate, setEndDate] = useState(() => new Date());

  useEffect(() => {
    if (!siteGroupId) return;
    setLoading(true);
    fetchDigitalMaintenanceReportsInRange(siteGroupId, moment(startDate).format(), moment(endDate).format())
      .then(data => {
        const next = (data || [])
          .map(row => {
            const ts = moment(row.created_time);
            return {
              id: row.id,
              moment_ms: ts.valueOf(),
              timestamp: ts.format('L, LTS'),
              site_id: row.site ?? 0,
              site_name: row.site_name ?? '',
              load_names: (row.load_names ?? []).join(', '),
              relay_numbers: (row.relay_numbers ?? []).join(', '),
              action: formatAction(row.command),
              user_name: formatUser(row.user),
            };
          })
          .sort((a, b) => b.moment_ms - a.moment_ms);
        setRows(next);
      })
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [siteGroupId, startDate, endDate]);

  const columns = useMemo(
    () => [
      { field: 'timestamp', headerName: t('general.timestamp'), width: 200 },
      {
        field: 'site_name',
        headerName: isBillboard
          ? t('reports.structure', { defaultValue: 'Structure' })
          : t('reports.site', { defaultValue: 'Site' }),
        flex: 1,
        minWidth: 160,
        renderCell: params => <Link to={`${baseStructureLink}${params.row.site_id}`}>{params.value}</Link>,
      },
      {
        field: 'load_names',
        headerName: relayLoadTerm.plural,
        flex: 1,
        minWidth: 140,
      },
      { field: 'relay_numbers', headerName: t('reports.relayNumber', { defaultValue: 'Relay #' }), width: 110 },
      { field: 'action', headerName: t('general.action', { defaultValue: 'Action' }), width: 110 },
      { field: 'user_name', headerName: t('general.user'), flex: 1, minWidth: 140 },
    ],
    [t, isBillboard, baseStructureLink, relayLoadTerm],
  );

  const handleDateRangeChange = useCallback((start, end) => {
    setStartDate(start);
    setEndDate(end);
  }, []);

  const handleExport = useCallback(() => {
    if (rows.length === 0) return;
    const headers = [
      [t('reports.digitalMaintenance')],
      [
        `${isBillboard ? t('general.plant', 'Plant') : t('general.siteGroup', 'Site Group')}: ${siteGroup?.name ?? ''}. ${siteGroup?.description ?? ''}`,
      ],
      [`${moment(startDate).format('L')} to ${moment(endDate).format('L')}`],
      [],
      columns.map(c => c.headerName),
    ];
    const dataColumns = columns.map(c => c.field);
    getExcelFile(rows, dataColumns, headers, `digital-maint-report-${Date.now()}`);
  }, [rows, columns, siteGroup, startDate, endDate, isBillboard, t]);

  const toolbarContent = useMemo(
    () => (
      <TimeRangeSelector
        startDate={startDate}
        endDate={endDate}
        onChange={handleDateRangeChange}
        disabled={loading}
        size="sm"
        exclude={['last1h', 'last24h']}
        defaultPreset="last30d"
      />
    ),
    [startDate, endDate, handleDateRangeChange, loading],
  );

  const exportButton = useMemo(
    () => (
      <ToolbarIconButton
        tooltip={t('general.export', { defaultValue: 'Export' })}
        onClick={handleExport}
        disabled={loading || rows.length === 0}
      >
        {loading ? <CircularProgress size={18} /> : <FileDownload sx={{ fontSize: 18 }} />}
      </ToolbarIconButton>
    ),
    [t, handleExport, loading, rows.length],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <DataGridToolbar
        apiRef={apiRef}
        toolbarContent={toolbarContent}
        toolbarActions={exportButton}
        quickFilterPlaceholder={t('general.search')}
      />
      <DataGridPro
        apiRef={apiRef}
        rows={rows}
        columns={columns}
        loading={loading}
        density="compact"
        hideFooter
        disableRowSelectionOnClick
        localeText={{ noRowsLabel: t('general.noResultsMessage') }}
        sx={{ flex: 1, minHeight: 0 }}
      />
    </Box>
  );
}

export default DigitalMaintenanceReportGrid;
